/**
 * 保留既有部门默认策略的稳定键和权限显示映射。
 * 角色策略与成员的系统部门归属独立保存；此映射不自行授予管理员身份。
 */
export const PHASE1_MODULE_KEYS = [
  'device',
  'package',
  'plan',
  'replacement',
  'quote',
  'customer',
  'settings',
  'inventory',
] as const;

export type Phase1ModuleKey = (typeof PHASE1_MODULE_KEYS)[number];
export type Phase1Access = 'edit' | 'read' | 'none' | 'custom';

export const PHASE1_MODULE_LABELS: Record<Phase1ModuleKey, string> = {
  device: '设备管理',
  package: '套餐管理',
  plan: '方案管理',
  inventory: '库存查看',
  replacement: '平替管理',
  quote: '报价管理',
  customer: '客户管理',
  settings: '设置',
};

export const PHASE1_DEPARTMENTS = [
  { key: 'admin', label: '管理员', roleKey: 'phase1_department_admin' },
  {
    key: 'procurement',
    label: '采销部',
    roleKey: 'phase1_department_procurement',
  },
  { key: 'customer', label: '客户部', roleKey: 'phase1_department_customer' },
  { key: 'business', label: '商务部', roleKey: 'phase1_department_business' },
  { key: 'ai', label: 'AI部', roleKey: 'phase1_department_ai' },
] as const;

export type Phase1DepartmentKey = (typeof PHASE1_DEPARTMENTS)[number]['key'];

const MODULE_PERMISSION_CODES: Record<
  Phase1ModuleKey,
  { read: readonly string[]; edit: readonly string[] }
> = {
  device: {
    read: ['equipment.read'],
    edit: ['equipment.create', 'equipment.edit', 'equipment.set_status'],
  },
  package: {
    read: ['package.read'],
    edit: ['package.create', 'package.edit', 'package.set_status'],
  },
  plan: {
    read: ['plan.read'],
    edit: ['plan.create', 'plan.edit', 'plan.set_status'],
  },
  inventory: { read: ['inventory.read'], edit: [] },
  replacement: {
    read: ['catalog.replacement.read'],
    edit: ['catalog.replacement.manage'],
  },
  quote: {
    read: ['quote.read'],
    edit: ['quote.create', 'quote.edit', 'quote.version.create'],
  },
  customer: { read: ['customer.read'], edit: ['customer.manage'] },
  settings: {
    read: ['settings.read'],
    edit: ['settings.manage'],
  },
};

export const INDEPENDENT_PERMISSION_ACTIONS = [
  ['equipment.delete', '删除设备'],
  ['package.delete', '删除套餐'],
  ['plan.delete', '删除方案'],
  ['catalog.replacement.delete', '删除平替池'],
  ['catalog.store_mapping.read', '查看门店映射'],
  ['catalog.store_mapping.manage', '维护门店映射'],
  ['quote.export', '导出报价'],
  ['quote.publish', '发布报价版本'],
  ['quote.share.external', '客户分享'],
  ['quote.share.internal', '内部分享'],
  ['quote.order_confirmation.read', '查看确认下单'],
  ['quote.order_confirmation.confirm', '确认下单'],
  ['quote.order_confirmation.ocr', '下单 OCR'],
  ['quote.archive', '归档/恢复报价'],
  ['identity.user.read', '查看用户'],
  ['identity.user.manage', '维护用户授权'],
  ['identity.role.read', '查看角色'],
  ['identity.role.manage', '维护角色权限'],
  ['identity.wecom.sync', '同步企业微信成员'],
  ['identity.department.manage', '维护部门'],
] as const;

/** Every operation controlled by this matrix must be removed before replacement. */
export const PHASE1_CONTROLLED_PERMISSION_CODES = new Set(
  PHASE1_MODULE_KEYS.flatMap((moduleKey) => [
    ...MODULE_PERMISSION_CODES[moduleKey].read,
    ...MODULE_PERMISSION_CODES[moduleKey].edit,
  ]),
);

export function permissionCodesForAccess(
  access: Phase1Access,
  moduleKey: Phase1ModuleKey,
): string[] {
  if (access === 'none' || access === 'custom') return [];
  const codes = MODULE_PERMISSION_CODES[moduleKey];
  return access === 'edit' ? [...codes.read, ...codes.edit] : [...codes.read];
}

export function mergePhase1PermissionCodes(
  existing: readonly string[],
  selected: readonly string[],
): string[] {
  return Array.from(
    new Set([
      ...existing.filter(
        (code) => !PHASE1_CONTROLLED_PERMISSION_CODES.has(code),
      ),
      ...selected,
    ]),
  ).sort();
}

/** Preserve independent grants and unknown codes; only explicit module edits replace basic codes. */
export function phase1RolePermissionCodes(
  _roleKey: string,
  existing: readonly string[],
  selected: readonly string[],
): string[] {
  return mergePhase1PermissionCodes(existing, selected);
}

export function moduleAccess(
  permissionCodes: readonly string[],
  moduleKey: Phase1ModuleKey,
): Phase1Access {
  const granted = new Set(permissionCodes);
  const codes = MODULE_PERMISSION_CODES[moduleKey];
  if (
    codes.edit.length &&
    [...codes.read, ...codes.edit].every((code) => granted.has(code))
  )
    return 'edit';
  if (codes.edit.some((code) => granted.has(code))) return 'custom';
  return codes.read.every((code) => granted.has(code)) ? 'read' : 'none';
}

export function updateMatrixPermissionCodes(
  existing: readonly string[],
  changes: Partial<Record<Phase1ModuleKey, Phase1Access>>,
  actionChanges: Readonly<Record<string, boolean>>,
): string[] {
  let next = [...existing];
  for (const moduleKey of PHASE1_MODULE_KEYS) {
    const access = changes[moduleKey];
    if (access === undefined || access === 'custom') continue;
    const controlled = [
      ...MODULE_PERMISSION_CODES[moduleKey].read,
      ...MODULE_PERMISSION_CODES[moduleKey].edit,
    ];
    next = next.filter((code) => !controlled.includes(code));
    next.push(...permissionCodesForAccess(access, moduleKey));
  }
  for (const [code, allowed] of Object.entries(actionChanges)) {
    next = next.filter((existingCode) => existingCode !== code);
    if (allowed) next.push(code);
  }
  return [...new Set(next)].sort();
}

export function phase1DepartmentOfRoleKey(
  roleKey: string,
): Phase1DepartmentKey | null {
  const found = PHASE1_DEPARTMENTS.find((entry) => entry.roleKey === roleKey);
  return found ? found.key : null;
}

export type Phase1DepartmentRow = {
  key: Phase1DepartmentKey;
  label: string;
  roleId: string;
  roleKey: string;
  active: boolean;
  dataScope: string;
  customerDataScope: string;
  access: Record<Phase1ModuleKey, Phase1Access>;
};

const DATA_SCOPE_LABELS: Record<string, string> = {
  all: '全部数据',
  self: '仅本人',
  self_assigned: '仅本人',
  department: '本部门',
  department_and_descendants: '本部门及下级',
  specified_departments: '指定部门',
  none: '无',
};

export function dataScopeLabel(
  dataScopes: readonly { resourceType: string; scopeType: string }[],
  resourceType: 'quote' | 'customer' = 'quote',
): string {
  const scope = dataScopes.find((item) => item.resourceType === resourceType);
  if (!scope) return '-';
  return DATA_SCOPE_LABELS[scope.scopeType] ?? scope.scopeType;
}

/** Order confirmation belongs to quote management and must share its scope. */
export function withPhase1QuoteDataScope<
  T extends {
    resourceType: string;
    scopeType: string;
  },
>(dataScopes: readonly T[], scopeType: 'all' | 'self_assigned') {
  return [
    ...dataScopes.filter((scope) => scope.resourceType !== 'quote'),
    { resourceType: 'quote', scopeType },
  ];
}

/** One customer data scope applies to both reading and editing. */
export function withPhase1CustomerDataScope<
  T extends { resourceType: string; scopeType: string },
>(dataScopes: readonly T[], scopeType: 'all' | 'self_assigned') {
  return [
    ...dataScopes.filter((scope) => scope.resourceType !== 'customer'),
    { resourceType: 'customer', scopeType },
  ];
}

export function buildPhase1DepartmentMatrix(
  roles: readonly {
    id: string;
    key: string;
    active: boolean;
    permissionCodes: readonly string[];
    dataScopes?: readonly { resourceType: string; scopeType: string }[];
  }[],
): Phase1DepartmentRow[] {
  return PHASE1_DEPARTMENTS.flatMap((department) => {
    const role = roles.find((item) => item.key === department.roleKey);
    if (!role) return [];
    return [
      {
        key: department.key,
        label: department.label,
        roleId: role.id,
        roleKey: role.key,
        active: role.active,
        dataScope: dataScopeLabel(role.dataScopes ?? []),
        customerDataScope: dataScopeLabel(role.dataScopes ?? [], 'customer'),
        access: Object.fromEntries(
          PHASE1_MODULE_KEYS.map((moduleKey) => [
            moduleKey,
            moduleAccess(role.permissionCodes, moduleKey),
          ]),
        ) as Record<Phase1ModuleKey, Phase1Access>,
      },
    ];
  });
}
